import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { useErrorHandler } from '../services/errorHandler';
import { useTranslation } from 'react-i18next';

interface FavoritesContextType {
  favoriteIds: Set<number>;
  isFavorite: (productId: number) => boolean;
  addToFavorites: (productId: number) => Promise<boolean>;
  removeFromFavorites: (productId: number) => Promise<boolean>;
  toggleFavorite: (productId: number) => Promise<boolean>;
  loadFavorites: () => Promise<void>;
  isLoading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const errorHandler = useErrorHandler();
  const { t } = useTranslation();

  // Load favorites from backend
  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavoriteIds(new Set());
      return;
    }

    try {
      const { data } = await api.get('/favorites');
      const favs = Array.isArray(data) ? data : [];
      const ids = new Set(favs.map((f: any) => f.ProductId || f.productId));
      setFavoriteIds(ids);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavoriteIds(new Set());
    }
  }, [isAuthenticated]);

  // Load favorites on mount and when auth changes
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Check if product is in favorites
  const isFavorite = useCallback((productId: number) => {
    return favoriteIds.has(productId);
  }, [favoriteIds]);

  // Add to favorites
  const addToFavorites = useCallback(async (productId: number): Promise<boolean> => {
    if (!isAuthenticated) {
      errorHandler.showWarning(t('auth.loginRequired', 'You must be logged in to perform this action'));
      return false;
    }

    // Optimistic update
    setFavoriteIds(prev => new Set(prev).add(productId));
    setIsLoading(true);

    try {
      await api.post('/favorites', { productId });
      errorHandler.showSuccess(t('favorites.added', 'Added to favorites'));
      return true;
    } catch (error) {
      // Revert optimistic update on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      errorHandler.handleError(error, { showToast: true });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, errorHandler, t]);

  // Remove from favorites
  const removeFromFavorites = useCallback(async (productId: number): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }

    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    setIsLoading(true);

    try {
      await api.delete(`/favorites/${productId}`);
      errorHandler.showSuccess(t('favorites.removed', 'Removed from favorites'));
      return true;
    } catch (error) {
      // Revert optimistic update on error
      setFavoriteIds(prev => new Set(prev).add(productId));
      errorHandler.handleError(error, { showToast: true });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, errorHandler, t]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (productId: number): Promise<boolean> => {
    if (!isAuthenticated) {
      errorHandler.showWarning(t('auth.loginRequired', 'You must be logged in to perform this action'));
      return false;
    }

    const isFav = favoriteIds.has(productId);
    
    if (isFav) {
      return await removeFromFavorites(productId);
    } else {
      return await addToFavorites(productId);
    }
  }, [isAuthenticated, favoriteIds, addToFavorites, removeFromFavorites, errorHandler, t]);

  const value: FavoritesContextType = {
    favoriteIds,
    isFavorite,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    loadFavorites,
    isLoading,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavoritesContext = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavoritesContext must be used within FavoritesProvider');
  }
  return context;
};

